import { type RefObject, useEffect } from "react";

/**
 * Overlay davranışının ORTAK çekirdeği (CLOS-95 `[056]`).
 *
 * ### Neden var
 *
 * Odak kapanı, ESC ve dış tıklama mantığı `Modal` içinde gömülüydü. Sırada aynı davranışı
 * isteyen altı kart var: `[058]` mobil drawer, `[059]`–`[065]` üst bar panelleri. Her biri
 * kendi kopyasını yazsaydı kriter 5 altı kez ihlal edilir ve erişilebilirlik davranışı
 * yerden yere değişirdi — hepsi biraz farklı, hiçbiri tam doğru.
 *
 * Çıkarma sırasında `Modal`'ın davranışı DEĞİŞMEDİ; testleri aynen yeşil kaldı. Kancalar
 * ölü kod değil: ilk tüketicisi `Modal`'ın kendisi.
 *
 * ### Neden iki ayrı kanca
 *
 * Modal ve drawer odak kapanı ister: arkadaki sayfa erişilemez olmalı. Popover ve menü
 * İSTEMEZ — sayfa hâlâ kullanılabilir durumdadır ve odağı hapsetmek klavye kullanıcısını
 * kilitler. Ama ikisi de ESC ve dış tıklama ile kapanır. Tek kancada birleştirilseydi
 * popover'lar odak kapanını taşımak zorunda kalır ya da bayrakla devre dışı bırakılırdı;
 * ikisi de yanlış.
 */

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(", ");

function odaklanabilirler(kap: HTMLElement): HTMLElement[] {
  return Array.from(kap.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
}

/**
 * Odağı verilen kabın içinde tutar; kapanınca önceki odağa döner.
 *
 * Yalnız ODAK yönetir — kapanma `useDismiss`'in işi. Ayrımın sebebi yukarıda.
 */
export function useFocusTrap(ref: RefObject<HTMLElement | null>, active: boolean): void {
  useEffect(() => {
    if (!active) return;
    const kap = ref.current;
    if (!kap) return;

    /**
     * Açılmadan önceki odak SAKLANIYOR. Kapanınca oraya dönmek, klavye kullanıcısının
     * sayfada kaybolmamasının tek yolu — aksi hâlde odak `<body>`'ye düşer ve kullanıcı
     * baştan gezinmek zorunda kalır.
     */
    const oncekiOdak = document.activeElement as HTMLElement | null;
    (odaklanabilirler(kap)[0] ?? kap).focus();

    function onKeyDown(event: KeyboardEvent) {
      if (event.key !== "Tab") return;
      const kapGuncel = ref.current;
      if (!kapGuncel) return;
      const liste = odaklanabilirler(kapGuncel);
      const ilk = liste[0];
      const son = liste[liste.length - 1];
      if (!ilk || !son) return;

      const aktif = document.activeElement;
      // `disari`: odak bir şekilde kaptan çıkmışsa Tab onu geri çekiyor.
      const disari = !kapGuncel.contains(aktif);
      if (event.shiftKey && (aktif === ilk || disari)) {
        event.preventDefault();
        son.focus();
      } else if (!event.shiftKey && (aktif === son || disari)) {
        event.preventDefault();
        ilk.focus();
      }
    }

    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      oncekiOdak?.focus();
    };
  }, [ref, active]);
}

export interface DismissOptions {
  /** ESC ile kapanma. Varsayılan açık. */
  escape?: boolean;
  /**
   * Kabın DIŞINA tıklayınca kapanma. Varsayılan KAPALI.
   *
   * `Modal` kendi yarı saydam örtüsüne tıklamayı zaten dinliyor; ikisi birden açık olsaydı
   * `onDismiss` iki kez çağrılırdı. Popover ve menüde ise örtü yok, orada açılacak.
   */
  outsideClick?: boolean;
}

/**
 * Overlay'i KAPATAN etkileşimler: ESC ve (istenirse) dışarı tıklama.
 *
 * Odak kapanından ayrı çünkü popover/menü kapanma davranışını ister, odak hapsini istemez.
 */
export function useDismiss(
  ref: RefObject<HTMLElement | null>,
  active: boolean,
  onDismiss: () => void,
  { escape = true, outsideClick = false }: DismissOptions = {},
): void {
  useEffect(() => {
    if (!active) return;

    function onKeyDown(event: KeyboardEvent) {
      if (!escape || event.key !== "Escape") return;
      /**
       * ⚠ `stopPropagation` KORUNUYOR: iç içe overlay'lerde ESC yalnız en üsttekini
       * kapatmalı. Kaldırılsaydı bir modal içindeki menüyü kapatan ESC modalı da
       * kapatırdı. (Davranış `Modal`'dan olduğu gibi taşındı.)
       */
      event.stopPropagation();
      onDismiss();
    }

    function onPointerDown(event: MouseEvent) {
      if (!outsideClick) return;
      const kap = ref.current;
      if (kap && !kap.contains(event.target as Node)) onDismiss();
    }

    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("mousedown", onPointerDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("mousedown", onPointerDown);
    };
  }, [ref, active, onDismiss, escape, outsideClick]);
}
